import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import { HDDRoute, RoutePoint, ProfilePoint, HDDParameters, HDDPathPoint } from './types';
import { generateElevationProfile } from './elevationProfile';
import { CrossSectionView } from './sectionView';
import { calculateHDDPath, getDefaultHDDParameters } from './hddCalculation';
import * as units from './units';
import { initializeAuth, isAuthenticated, login, isAuthConfigured, getUserDisplayName } from './auth';
import './styles.css';

// Current active screen
let currentScreen = 'dashboard';

// IMPORTANT: Replace with your Mapbox token
mapboxgl.accessToken = 'pk.eyJ1IjoiYmFydG9uaXEiLCJhIjoiY21tMjc0cnUwMDRlaDJ1cjB1cWFoeXdjeCJ9.6pGhbJCs7SRc3r24kunmNA';

let map: mapboxgl.Map;
let planViewMap: mapboxgl.Map | null = null;
let draw: MapboxDraw;
let currentRoute: HDDRoute | null = null;
let elevationProfile: ProfilePoint[] | null = null;
let hddParameters: HDDParameters = getDefaultHDDParameters();
let hddPath: HDDPathPoint[] | null = null;
let drawnFeatureId: string | null = null;
let originalWaypoints: [number, number][] = [];  // Store original waypoints for editing
let isEditingRoute: boolean = false;
let waypointMarkers: mapboxgl.Marker[] = [];

// Dashboard map showing active job locations
let dashboardMap: mapboxgl.Map | null = null;

// Live Ops job maps
let jobMaps: Map<string, mapboxgl.Map> = new Map();
let jobMapsInitialized = false;

// Auth and app initialization
async function initApp() {
  const authScreen = document.getElementById('auth-screen')!;
  const authStatus = document.getElementById('auth-status')!;
  const authLoginBtn = document.getElementById('auth-login-btn')!;
  const appShell = document.querySelector('.app-shell') as HTMLElement;

  // If auth is not configured, skip auth screen entirely
  if (!isAuthConfigured()) {
    authScreen.style.display = 'none';
    appShell.style.display = 'grid';
    initializeAppShell();
    return;
  }

  // Show auth screen while checking
  authScreen.style.display = 'flex';

  // Initialize auth
  await initializeAuth();

  // Check if authenticated
  if (!isAuthenticated()) {
    authStatus.textContent = 'Please sign in to continue';
    authLoginBtn.style.display = 'inline-block';
    authLoginBtn.addEventListener('click', () => {
      authStatus.textContent = 'Redirecting to sign in...';
      authLoginBtn.style.display = 'none';
      login();
    });
    return; // Don't initialize the app until authenticated
  }

  // User is authenticated - hide auth screen and show app shell
  authScreen.style.display = 'none';
  appShell.style.display = 'grid';

  // Update user display name
  const userDisplayName = document.getElementById('user-display-name');
  if (userDisplayName) {
    const name = getUserDisplayName();
    if (name) {
      userDisplayName.textContent = name;
    }
  }

  // Initialize the app shell and navigation
  initializeAppShell();
}

// Initialize the application shell with navigation
function initializeAppShell() {
  // Set up navigation
  setupNavigation();

  // Initialize dashboard map
  initializeDashboardMap();

  // Show the default screen (dashboard)
  switchScreen('dashboard');
}

// Set up sidebar navigation
function setupNavigation() {
  const navItems = document.querySelectorAll('.nav-item[data-screen]');

  navItems.forEach(item => {
    item.addEventListener('click', () => {
      const screenId = item.getAttribute('data-screen');
      if (screenId) {
        switchScreen(screenId);
      }
    });
  });

  // Quick action buttons on dashboard
  const actionBtns = document.querySelectorAll('.action-btn[data-screen]');
  actionBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const screenId = btn.getAttribute('data-screen');
      if (screenId) {
        switchScreen(screenId);
      }
    });
  });

  // Initialize hydraulics tabs
  initializeHydraulicsTabs();

  // Initialize business tabs
  initializeBusinessTabs();

  // Initialize R&M tabs
  initializeRMTabs();
}

// Initialize hydraulics tab switching
function initializeHydraulicsTabs() {
  const hydroTabs = document.querySelectorAll('.hydro-tab');
  const tabContents = document.querySelectorAll('.hydro-tab-content');

  hydroTabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const tabId = tab.getAttribute('data-tab');
      if (!tabId) return;

      // Update tab active states
      hydroTabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // Update content visibility
      tabContents.forEach(content => {
        content.classList.remove('active');
        if (content.id === `tab-${tabId}`) {
          content.classList.add('active');
        }
      });
    });
  });
}

// Initialize business tab switching
function initializeBusinessTabs() {
  const bizTabs = document.querySelectorAll('.biz-tab');
  const tabContents = document.querySelectorAll('.biz-tab-content');

  bizTabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const tabId = tab.getAttribute('data-tab');
      if (!tabId) return;

      // Update tab active states
      bizTabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // Update content visibility
      tabContents.forEach(content => {
        content.classList.remove('active');
        if (content.id === `tab-${tabId}`) {
          content.classList.add('active');
        }
      });
    });
  });
}

// Initialize R&M tab switching
function initializeRMTabs() {
  const rmTabs = document.querySelectorAll('.rm-tab');
  const tabContents = document.querySelectorAll('.rm-tab-content');

  rmTabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const tabId = tab.getAttribute('data-tab');
      if (!tabId) return;

      // Update tab active states
      rmTabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // Update content visibility
      tabContents.forEach(content => {
        content.classList.remove('active');
        if (content.id === `tab-${tabId}`) {
          content.classList.add('active');
        }
      });
    });
  });
}

// Switch between screens
function switchScreen(screenId: string) {
  // Update navigation active state
  document.querySelectorAll('.nav-item').forEach(item => {
    item.classList.remove('active');
    if (item.getAttribute('data-screen') === screenId) {
      item.classList.add('active');
    }
  });

  // Hide all screens
  document.querySelectorAll('.screen').forEach(screen => {
    screen.classList.remove('active');
  });

  // Show the selected screen
  const targetScreen = document.getElementById(`screen-${screenId}`);
  if (targetScreen) {
    targetScreen.classList.add('active');
  }

  // Initialize map when switching to route planner (lazy initialization)
  if (screenId === 'route-planner' && !map) {
    initializeMap();
  }

  // Resize maps when switching screens (they need to recalculate size)
  if (screenId === 'route-planner' && map) {
    setTimeout(() => map.resize(), 100);
  }

  // Initialize job maps when switching to live-ops (lazy initialization)
  if (screenId === 'live-ops' && !jobMapsInitialized) {
    setTimeout(() => initializeJobMaps(), 100);
  }

  // Resize job maps when switching to live-ops
  if (screenId === 'live-ops' && jobMapsInitialized) {
    jobMaps.forEach(jobMap => jobMap.resize());
  }

  currentScreen = screenId;
}

// Initialize mini maps for each job in Live Ops
function initializeJobMaps() {
  const jobCards = document.querySelectorAll('.job-card[data-job-id]');

  jobCards.forEach(card => {
    const jobId = card.getAttribute('data-job-id');
    const lat = parseFloat(card.getAttribute('data-lat') || '0');
    const lng = parseFloat(card.getAttribute('data-lng') || '0');
    const mapContainer = card.querySelector('.job-map') as HTMLElement;

    if (!jobId || !mapContainer || jobMaps.has(jobId)) return;

    const jobMap = new mapboxgl.Map({
      container: mapContainer,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [lng, lat],
      zoom: 14,
      interactive: false,
      attributionControl: false
    });

    // Add a marker for the job location (bit position)
    jobMap.on('load', () => {
      const el = document.createElement('div');
      el.style.cssText = `
        width: 14px;
        height: 14px;
        background: #22c55e;
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.4);
      `;

      new mapboxgl.Marker(el)
        .setLngLat([lng, lat])
        .addTo(jobMap);

      // Add a mock route line
      const routeOffset = 0.005; // ~500m offset for mock route
      jobMap.addSource('job-route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [
              [lng - routeOffset, lat - routeOffset * 0.5],
              [lng, lat],
              [lng + routeOffset, lat + routeOffset * 0.3]
            ]
          }
        }
      });

      jobMap.addLayer({
        id: 'job-route-line',
        type: 'line',
        source: 'job-route',
        paint: {
          'line-color': '#FF4500',
          'line-width': 3
        }
      });
    });

    jobMaps.set(jobId, jobMap);
  });

  jobMapsInitialized = true;
}

function initializeDashboardMap() {
  const dashboardMapContainer = document.getElementById('dashboard-map');
  if (!dashboardMapContainer) return;

  // Clear placeholder text
  dashboardMapContainer.innerHTML = '';

  dashboardMap = new mapboxgl.Map({
    container: 'dashboard-map',
    style: 'mapbox://styles/mapbox/dark-v11',
    center: [-98.5795, 39.8283], // Center of USA
    zoom: 3.5,
    interactive: true,
    attributionControl: false
  });

  // Add mock job markers
  const mockJobs = [
    { name: 'Houston Pipeline', coords: [-95.3698, 29.7604], status: 'drilling' },
    { name: 'Denver Crossing', coords: [-104.9903, 39.7392], status: 'reaming' },
    { name: 'Phoenix Highway', coords: [-112.0740, 33.4484], status: 'drilling' },
    { name: 'Dallas Creek', coords: [-96.7970, 32.7767], status: 'standby' },
    { name: 'Seattle Bay', coords: [-122.3321, 47.6062], status: 'reaming' }
  ];

  dashboardMap.on('load', () => {
    mockJobs.forEach(job => {
      const el = document.createElement('div');
      el.className = 'job-marker';

      let color = '#3b82f6'; // blue default
      if (job.status === 'drilling') color = '#22c55e'; // green
      else if (job.status === 'reaming') color = '#a855f7'; // purple
      else if (job.status === 'standby') color = '#f59e0b'; // amber

      el.style.cssText = `
        width: 16px;
        height: 16px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
      `;

      new mapboxgl.Marker(el)
        .setLngLat(job.coords as [number, number])
        .setPopup(new mapboxgl.Popup({ offset: 15 }).setHTML(`
          <strong>${job.name}</strong><br>
          <span style="text-transform: capitalize">${job.status}</span>
        `))
        .addTo(dashboardMap!);
    });
  });
}

function initializeMap() {
// Initialize map with default location (will update to user location if available)
map = new mapboxgl.Map({
  container: 'map',
  style: 'mapbox://styles/mapbox/satellite-streets-v12',
  center: [1.3, 52.6], // Default fallback: Leiston area
  zoom: 13,
  pitch: 0
});

// Add navigation controls
map.addControl(new mapboxgl.NavigationControl(), 'top-right');

// Add geolocation control
const geolocateControl = new mapboxgl.GeolocateControl({
  positionOptions: {
    enableHighAccuracy: true
  },
  trackUserLocation: false,
  showUserHeading: false
});
map.addControl(geolocateControl, 'top-right');

// Add draw control for interactive line drawing
draw = new MapboxDraw({
  displayControlsDefault: false,
  controls: {
    line_string: true,
    trash: true
  },
  defaultMode: 'draw_line_string',
  styles: [
    // Line style
    {
      id: 'gl-draw-line',
      type: 'line',
      filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': '#0080ff',
        'line-width': 4
      }
    },
    // Vertex point style
    {
      id: 'gl-draw-point',
      type: 'circle',
      filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex']],
      paint: {
        'circle-radius': 8,
        'circle-color': '#fff',
        'circle-stroke-color': '#0080ff',
        'circle-stroke-width': 3
      }
    },
    // Midpoint style (for adding new vertices)
    {
      id: 'gl-draw-point-midpoint',
      type: 'circle',
      filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
      paint: {
        'circle-radius': 5,
        'circle-color': '#0080ff',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2
      }
    }
  ]
});
map.addControl(draw, 'top-left');

// Custom Home Control
class HomeControl implements mapboxgl.IControl {
  private container: HTMLDivElement | undefined;

  onAdd(): HTMLElement {
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

    const button = document.createElement('button');
    button.type = 'button';
    button.title = 'Back to Home';
    button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>`;
    button.addEventListener('click', () => {
      window.location.href = '/';
    });

    this.container.appendChild(button);
    return this.container;
  }

  onRemove(): void {
    this.container?.parentNode?.removeChild(this.container);
  }
}

// Custom Units Toggle Control
class UnitsControl implements mapboxgl.IControl {
  private container: HTMLDivElement | undefined;
  private button: HTMLButtonElement | undefined;

  onAdd(): HTMLElement {
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.id = 'units-ctrl-btn';
    this.button.title = 'Toggle metric/imperial units';
    this.updateButtonText();

    this.button.addEventListener('click', () => {
      handleUnitToggle();
      this.updateButtonText();
    });

    this.container.appendChild(this.button);
    return this.container;
  }

  private updateButtonText(): void {
    if (this.button) {
      this.button.textContent = units.isMetric() ? 'm' : 'ft';
      this.button.classList.toggle('imperial', !units.isMetric());
    }
  }

  onRemove(): void {
    this.container?.parentNode?.removeChild(this.container);
  }
}

// Add custom controls above draw controls
map.addControl(new HomeControl(), 'top-left');
map.addControl(new UnitsControl(), 'top-left');

// Try to get user's location on load
if (navigator.geolocation) {
  navigator.geolocation.getCurrentPosition(
    (position) => {
      map.flyTo({
        center: [position.coords.longitude, position.coords.latitude],
        zoom: 14,
        duration: 1500
      });
    },
    (error) => {
      console.log('Geolocation not available or denied:', error.message);
      // Keep default location
    },
    {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0
    }
  );
}

// Add terrain and 3D buildings on load
map.on('load', () => {
  // Add terrain source
  map.addSource('mapbox-dem', {
    type: 'raster-dem',
    url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
    tileSize: 512,
    maxzoom: 14
  });
  
  map.setTerrain({ source: 'mapbox-dem', exaggeration: 1 });
  
  // Add 3D buildings if not already present
  const layers = map.getStyle().layers;
  const buildingLayer = layers?.find(layer => layer.id === 'building');
  
  if (buildingLayer && !layers?.find(layer => layer.id === '3d-buildings')) {
    map.addLayer({
      id: '3d-buildings',
      source: 'composite',
      'source-layer': 'building',
      filter: ['==', 'extrude', 'true'],
      type: 'fill-extrusion',
      minzoom: 15,
      paint: {
        'fill-extrusion-color': '#aaa',
        'fill-extrusion-height': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15,
          0,
          15.05,
          ['get', 'height']
        ],
        'fill-extrusion-base': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15,
          0,
          15.05,
          ['get', 'min_height']
        ],
        'fill-extrusion-opacity': 0.6
      }
    });
  }
});

// Draw event handlers
map.on('draw.create', updateRouteFromDraw);
map.on('draw.update', updateRouteFromDraw);
map.on('draw.delete', () => {
  currentRoute = null;
  elevationProfile = null;
  drawnFeatureId = null;
  updateInstructions('Click the line tool to draw a route');
  document.getElementById('view-section-btn')!.setAttribute('disabled', 'true');
  document.getElementById('route-info')!.style.display = 'none';

  // Remove curved route layer if exists
  if (map.getSource('curved-route')) {
    map.removeLayer('curved-route-line');
    map.removeSource('curved-route');
  }
});

async function updateRouteFromDraw() {
  const data = draw.getAll();
  if (data.features.length === 0) return;

  const feature = data.features[0];
  if (feature.geometry.type !== 'LineString') return;

  drawnFeatureId = feature.id as string;
  const coords = feature.geometry.coordinates as [number, number][];

  if (coords.length < 2) {
    updateInstructions('Click to add more points to your route');
    return;
  }

  // Store original waypoints for later editing
  originalWaypoints = [...coords];

  // Get elevations for start and end points
  const startElev = await getElevation(new mapboxgl.LngLat(coords[0][0], coords[0][1]));
  const endElev = await getElevation(new mapboxgl.LngLat(coords[coords.length - 1][0], coords[coords.length - 1][1]));

  const startPoint: RoutePoint = {
    longitude: coords[0][0],
    latitude: coords[0][1],
    elevation: startElev
  };

  const endPoint: RoutePoint = {
    longitude: coords[coords.length - 1][0],
    latitude: coords[coords.length - 1][1],
    elevation: endElev
  };

  // Create smooth curve through all points
  const curvedCoords = createSmoothCurve(coords);
  const line = turf.lineString(curvedCoords);

  currentRoute = {
    start: startPoint,
    end: endPoint,
    distance: turf.length(line, { units: 'meters' }),
    path: line.geometry
  };

  // Display the smooth curved route
  displayCurvedRoute(curvedCoords);

  // Clear cached elevation profile when route changes
  elevationProfile = null;

  // Update UI
  updateInstructions('Route created. Drag points to adjust. Click "View Cross Section" to analyze.');
  document.getElementById('view-section-btn')!.removeAttribute('disabled');

  const routeInfo = document.getElementById('route-info')!;
  routeInfo.style.display = 'block';
  document.getElementById('route-distance')!.textContent = units.convertDistance(currentRoute.distance).toFixed(1);
}

// Create smooth curve through points using Catmull-Rom spline
function createSmoothCurve(points: [number, number][]): [number, number][] {
  if (points.length < 2) return points;
  if (points.length === 2) return points;

  const result: [number, number][] = [];
  const numSegments = 20; // Points per segment

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    for (let t = 0; t < numSegments; t++) {
      const s = t / numSegments;
      const s2 = s * s;
      const s3 = s2 * s;

      // Catmull-Rom spline formula
      const x = 0.5 * (
        (2 * p1[0]) +
        (-p0[0] + p2[0]) * s +
        (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * s2 +
        (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * s3
      );
      const y = 0.5 * (
        (2 * p1[1]) +
        (-p0[1] + p2[1]) * s +
        (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * s2 +
        (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * s3
      );

      result.push([x, y]);
    }
  }

  // Add the last point
  result.push(points[points.length - 1]);

  return result;
}

function displayCurvedRoute(coords: [number, number][]) {
  // Remove existing curved route layer
  if (map.getSource('curved-route')) {
    map.removeLayer('curved-route-line');
    map.removeSource('curved-route');
  }

  map.addSource('curved-route', {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: coords
      }
    }
  });

  // Check if draw layer exists before trying to insert below it
  const beforeLayer = map.getLayer('gl-draw-line') ? 'gl-draw-line' : undefined;

  map.addLayer({
    id: 'curved-route-line',
    type: 'line',
    source: 'curved-route',
    paint: {
      'line-color': '#22c55e',
      'line-width': 3,
      'line-dasharray': [4, 2]
    }
  }, beforeLayer);
}

async function getElevation(lngLat: mapboxgl.LngLat): Promise<number> {
  return map.queryTerrainElevation(lngLat, { exaggerated: false }) || 0;
}

function updateInstructions(text: string) {
  document.getElementById('instructions')!.textContent = text;
}

function showLoadingSpinner() {
  document.getElementById('loading-spinner')!.style.display = 'block';
}

function hideLoadingSpinner() {
  document.getElementById('loading-spinner')!.style.display = 'none';
}

// View section button
document.getElementById('view-section-btn')!.addEventListener('click', async () => {
  if (!currentRoute) return;

  // Hide map view, show section view (within route planner screen)
  const mapContainer = document.getElementById('map-container')!;
  const sectionContainer = document.getElementById('section-container')!;
  mapContainer.style.display = 'none';
  sectionContainer.style.display = 'block';

  // Generate elevation profile if not already done
  if (!elevationProfile) {
    showLoadingSpinner();
    try {
      elevationProfile = await generateElevationProfile(map, currentRoute, 10);
    } catch (error) {
      console.error('Error generating profile:', error);
      alert('Error generating elevation profile. Please try again.');
      hideLoadingSpinner();
      return;
    }
    hideLoadingSpinner();
  }

  // Update section info
  document.getElementById('section-distance')!.textContent =
    units.convertDistance(currentRoute.distance).toFixed(1);

  const buildingCount = elevationProfile.reduce((count, p) =>
    count + p.surfaceFeatures.filter(f => f.type === 'building').length, 0);
  // Count both waterways (linear) and water bodies (polygons)
  const waterwayCount = elevationProfile.reduce((count, p) =>
    count + p.surfaceFeatures.filter(f => f.type === 'waterway' || f.type === 'water').length, 0);
  const roadCount = elevationProfile.reduce((count, p) =>
    count + p.surfaceFeatures.filter(f => f.type === 'road').length, 0);
  const railwayCount = elevationProfile.reduce((count, p) =>
    count + p.surfaceFeatures.filter(f => f.type === 'railway').length, 0);

  document.getElementById('building-count')!.textContent = buildingCount.toString();
  document.getElementById('waterway-count')!.textContent = waterwayCount.toString();
  document.getElementById('road-count')!.textContent = roadCount.toString();
  document.getElementById('railway-count')!.textContent = railwayCount.toString();

  // Calculate HDD path
  hddPath = calculateHDDPath(elevationProfile, hddParameters);

  // Render cross-section with HDD path
  new CrossSectionView('#section-view', elevationProfile, {}, hddPath);

  // Create/update plan view map
  createPlanView(currentRoute, elevationProfile);
});

// Create the plan view mini-map
function createPlanView(route: HDDRoute, profile: ProfilePoint[]) {
  // Remove existing map if present
  if (planViewMap) {
    planViewMap.remove();
    planViewMap = null;
  }

  // Calculate bounds from route
  const coords = route.path.coordinates as [number, number][];
  const bounds = coords.reduce(
    (bounds, coord) => bounds.extend(coord as [number, number]),
    new mapboxgl.LngLatBounds(coords[0], coords[0])
  );

  // Create the plan view map
  planViewMap = new mapboxgl.Map({
    container: 'plan-view',
    style: 'mapbox://styles/mapbox/satellite-streets-v12',
    bounds: bounds,
    fitBoundsOptions: { padding: 50 },
    interactive: true,
    attributionControl: false
  });

  // Add navigation controls
  planViewMap.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

  planViewMap.on('load', () => {
    // Add the route line
    planViewMap!.addSource('plan-route', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: route.path
      }
    });

    planViewMap!.addLayer({
      id: 'plan-route-line',
      type: 'line',
      source: 'plan-route',
      paint: {
        'line-color': '#FF4500',
        'line-width': 4
      }
    });

    // Add start marker
    const startEl = document.createElement('div');
    startEl.className = 'plan-marker start-marker';
    startEl.style.cssText = 'width: 16px; height: 16px; background: #22c55e; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);';

    new mapboxgl.Marker(startEl)
      .setLngLat([route.start.longitude, route.start.latitude])
      .setPopup(new mapboxgl.Popup({ offset: 15 }).setHTML('<strong>Entry Point</strong>'))
      .addTo(planViewMap!);

    // Add end marker
    const endEl = document.createElement('div');
    endEl.className = 'plan-marker end-marker';
    endEl.style.cssText = 'width: 16px; height: 16px; background: #ef4444; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);';

    new mapboxgl.Marker(endEl)
      .setLngLat([route.end.longitude, route.end.latitude])
      .setPopup(new mapboxgl.Popup({ offset: 15 }).setHTML('<strong>Exit Point</strong>'))
      .addTo(planViewMap!);

    // Add building markers along route
    const buildingPoints: [number, number][] = [];
    profile.forEach(p => {
      if (p.surfaceFeatures.some(f => f.type === 'building')) {
        buildingPoints.push([p.longitude, p.latitude]);
      }
    });

    if (buildingPoints.length > 0) {
      planViewMap!.addSource('buildings-points', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: buildingPoints.map(coord => ({
            type: 'Feature' as const,
            properties: {},
            geometry: {
              type: 'Point' as const,
              coordinates: coord
            }
          }))
        }
      });

      planViewMap!.addLayer({
        id: 'buildings-circles',
        type: 'circle',
        source: 'buildings-points',
        paint: {
          'circle-radius': 6,
          'circle-color': '#666',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1
        }
      });
    }

    // Add water crossing markers
    const waterPoints: [number, number][] = [];
    profile.forEach(p => {
      if (p.surfaceFeatures.some(f => f.type === 'water' || f.type === 'waterway')) {
        waterPoints.push([p.longitude, p.latitude]);
      }
    });

    if (waterPoints.length > 0) {
      planViewMap!.addSource('water-points', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: waterPoints.map(coord => ({
            type: 'Feature' as const,
            properties: {},
            geometry: {
              type: 'Point' as const,
              coordinates: coord
            }
          }))
        }
      });

      planViewMap!.addLayer({
        id: 'water-circles',
        type: 'circle',
        source: 'water-points',
        paint: {
          'circle-radius': 6,
          'circle-color': '#1E90FF',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1
        }
      });
    }

    // Add road crossing markers
    const roadPoints: [number, number][] = [];
    profile.forEach(p => {
      if (p.surfaceFeatures.some(f => f.type === 'road')) {
        roadPoints.push([p.longitude, p.latitude]);
      }
    });

    if (roadPoints.length > 0) {
      planViewMap!.addSource('road-points', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: roadPoints.map(coord => ({
            type: 'Feature' as const,
            properties: {},
            geometry: {
              type: 'Point' as const,
              coordinates: coord
            }
          }))
        }
      });

      planViewMap!.addLayer({
        id: 'road-circles',
        type: 'circle',
        source: 'road-points',
        paint: {
          'circle-radius': 5,
          'circle-color': '#444',
          'circle-stroke-color': '#FFD700',
          'circle-stroke-width': 2
        }
      });
    }

    // Add railway crossing markers
    const railwayPoints: [number, number][] = [];
    profile.forEach(p => {
      if (p.surfaceFeatures.some(f => f.type === 'railway')) {
        railwayPoints.push([p.longitude, p.latitude]);
      }
    });

    if (railwayPoints.length > 0) {
      planViewMap!.addSource('railway-points', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: railwayPoints.map(coord => ({
            type: 'Feature' as const,
            properties: {},
            geometry: {
              type: 'Point' as const,
              coordinates: coord
            }
          }))
        }
      });

      planViewMap!.addLayer({
        id: 'railway-circles',
        type: 'circle',
        source: 'railway-points',
        paint: {
          'circle-radius': 6,
          'circle-color': '#8B7355',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 2
        }
      });
    }

    // Add distance markers every 100m
    addDistanceMarkers(planViewMap!, route, profile);
  });
}

// Add distance markers along the route
function addDistanceMarkers(map: mapboxgl.Map, route: HDDRoute, profile: ProfilePoint[]) {
  const markerInterval = units.getMarkerInterval(); // meters (adjusted for unit system)
  const totalDistance = route.distance;
  const unitLabel = units.distanceUnit();

  const markers: { coord: [number, number]; distance: number; displayDistance: string }[] = [];

  for (let d = markerInterval; d < totalDistance; d += markerInterval) {
    // Find the profile point closest to this distance
    let closest = profile[0];
    let minDiff = Infinity;

    for (const p of profile) {
      const diff = Math.abs(p.distance - d);
      if (diff < minDiff) {
        minDiff = diff;
        closest = p;
      }
    }

    markers.push({
      coord: [closest.longitude, closest.latitude],
      distance: d,
      displayDistance: units.formatMarkerDistance(d)
    });
  }

  if (markers.length > 0) {
    map.addSource('distance-markers', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: markers.map(m => ({
          type: 'Feature' as const,
          properties: { distance: m.distance, label: m.displayDistance },
          geometry: {
            type: 'Point' as const,
            coordinates: m.coord
          }
        }))
      }
    });

    map.addLayer({
      id: 'distance-labels',
      type: 'symbol',
      source: 'distance-markers',
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 10,
        'text-offset': [0, 1.5],
        'text-anchor': 'top'
      },
      paint: {
        'text-color': '#fff',
        'text-halo-color': '#333',
        'text-halo-width': 1
      }
    });
  }
}

// Route editing functions
function enterEditMode() {
  if (!planViewMap || originalWaypoints.length < 2) return;

  isEditingRoute = true;

  // Hide feature markers
  const layersToHide = ['buildings-circles', 'water-circles', 'road-circles', 'railway-circles', 'distance-labels'];
  layersToHide.forEach(layerId => {
    if (planViewMap!.getLayer(layerId)) {
      planViewMap!.setLayoutProperty(layerId, 'visibility', 'none');
    }
  });

  // Create draggable markers for each waypoint
  waypointMarkers = originalWaypoints.map((coord, index) => {
    const el = document.createElement('div');
    el.className = 'waypoint-marker';

    // Style based on position (start, middle, end)
    let color = '#3B82F6'; // Blue for middle points
    let size = 14;
    if (index === 0) {
      color = '#22c55e'; // Green for start
      size = 18;
    } else if (index === originalWaypoints.length - 1) {
      color = '#ef4444'; // Red for end
      size = 18;
    }

    el.style.cssText = `
      width: ${size}px;
      height: ${size}px;
      background: ${color};
      border: 3px solid white;
      border-radius: 50%;
      cursor: move;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    `;

    const marker = new mapboxgl.Marker({
      element: el,
      draggable: true
    })
      .setLngLat(coord)
      .addTo(planViewMap!);

    // Update route preview on drag
    marker.on('drag', () => {
      updateRoutePreview();
    });

    return marker;
  });

  // Update UI
  document.getElementById('adjust-route-btn')!.style.display = 'none';
  document.getElementById('apply-route-btn')!.style.display = 'inline-block';
  document.getElementById('cancel-adjust-btn')!.style.display = 'inline-block';
  document.getElementById('export-btn')!.style.display = 'none';
}

function updateRoutePreview() {
  if (!planViewMap) return;

  // Get current marker positions
  const newCoords = waypointMarkers.map(m => m.getLngLat().toArray() as [number, number]);

  // Create smooth curve through waypoints
  const curvedCoords = createSmoothCurve(newCoords);

  // Update the route line on the plan view
  const source = planViewMap.getSource('plan-route') as mapboxgl.GeoJSONSource;
  if (source) {
    source.setData({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: curvedCoords
      }
    });
  }
}

function exitEditMode(apply: boolean) {
  if (!planViewMap) return;

  if (apply && waypointMarkers.length >= 2) {
    // Get new waypoint positions
    originalWaypoints = waypointMarkers.map(m => m.getLngLat().toArray() as [number, number]);

    // Clear existing markers
    clearWaypointMarkers();

    // Recalculate the route
    recalculateRoute();
  } else {
    // Cancel - restore original route preview
    clearWaypointMarkers();

    // Restore original route on plan view
    if (currentRoute) {
      const source = planViewMap.getSource('plan-route') as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: currentRoute.path
        });
      }
    }

    // Show feature markers again
    const layersToShow = ['buildings-circles', 'water-circles', 'road-circles', 'railway-circles', 'distance-labels'];
    layersToShow.forEach(layerId => {
      if (planViewMap!.getLayer(layerId)) {
        planViewMap!.setLayoutProperty(layerId, 'visibility', 'visible');
      }
    });
  }

  isEditingRoute = false;

  // Update UI
  document.getElementById('adjust-route-btn')!.style.display = 'inline-block';
  document.getElementById('apply-route-btn')!.style.display = 'none';
  document.getElementById('cancel-adjust-btn')!.style.display = 'none';
  document.getElementById('export-btn')!.style.display = 'inline-block';
}

function clearWaypointMarkers() {
  waypointMarkers.forEach(m => m.remove());
  waypointMarkers = [];
}

async function recalculateRoute() {
  if (originalWaypoints.length < 2) return;

  showLoadingSpinner();

  try {
    // Get elevations for start and end points
    const startElev = await getElevation(new mapboxgl.LngLat(originalWaypoints[0][0], originalWaypoints[0][1]));
    const endElev = await getElevation(new mapboxgl.LngLat(
      originalWaypoints[originalWaypoints.length - 1][0],
      originalWaypoints[originalWaypoints.length - 1][1]
    ));

    const startPoint: RoutePoint = {
      longitude: originalWaypoints[0][0],
      latitude: originalWaypoints[0][1],
      elevation: startElev
    };

    const endPoint: RoutePoint = {
      longitude: originalWaypoints[originalWaypoints.length - 1][0],
      latitude: originalWaypoints[originalWaypoints.length - 1][1],
      elevation: endElev
    };

    // Create smooth curve through all points
    const curvedCoords = createSmoothCurve(originalWaypoints);
    const line = turf.lineString(curvedCoords);

    currentRoute = {
      start: startPoint,
      end: endPoint,
      distance: turf.length(line, { units: 'meters' }),
      path: line.geometry
    };

    // Update main map's draw feature
    if (drawnFeatureId) {
      draw.delete(drawnFeatureId);
    }
    const newFeature = draw.add({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: originalWaypoints
      }
    });
    drawnFeatureId = newFeature[0];

    // Update curved route on main map
    displayCurvedRoute(curvedCoords);

    // Regenerate elevation profile
    elevationProfile = await generateElevationProfile(map, currentRoute, 10);

    // Update section info
    document.getElementById('section-distance')!.textContent = units.convertDistance(currentRoute.distance).toFixed(1);

    const buildingCount = elevationProfile.reduce((count, p) =>
      count + p.surfaceFeatures.filter(f => f.type === 'building').length, 0);
    const waterwayCount = elevationProfile.reduce((count, p) =>
      count + p.surfaceFeatures.filter(f => f.type === 'waterway' || f.type === 'water').length, 0);
    const roadCount = elevationProfile.reduce((count, p) =>
      count + p.surfaceFeatures.filter(f => f.type === 'road').length, 0);
    const railwayCount = elevationProfile.reduce((count, p) =>
      count + p.surfaceFeatures.filter(f => f.type === 'railway').length, 0);

    document.getElementById('building-count')!.textContent = buildingCount.toString();
    document.getElementById('waterway-count')!.textContent = waterwayCount.toString();
    document.getElementById('road-count')!.textContent = roadCount.toString();
    document.getElementById('railway-count')!.textContent = railwayCount.toString();

    // Recalculate HDD path
    hddPath = calculateHDDPath(elevationProfile, hddParameters);

    // Re-render cross-section
    new CrossSectionView('#section-view', elevationProfile, {}, hddPath);

    // Recreate plan view with new data
    createPlanView(currentRoute, elevationProfile);

    // Update route info on main map view
    document.getElementById('route-distance')!.textContent = units.convertDistance(currentRoute.distance).toFixed(1);

  } catch (error) {
    console.error('Error recalculating route:', error);
    alert('Error recalculating route. Please try again.');
  }

  hideLoadingSpinner();
}

// Adjust route button
document.getElementById('adjust-route-btn')!.addEventListener('click', enterEditMode);

// Apply changes button
document.getElementById('apply-route-btn')!.addEventListener('click', () => exitEditMode(true));

// Cancel adjust button
document.getElementById('cancel-adjust-btn')!.addEventListener('click', () => exitEditMode(false));

// Back to map button
document.getElementById('view-map-btn')!.addEventListener('click', () => {
  // Exit edit mode if active
  if (isEditingRoute) {
    exitEditMode(false);
  }

  // Show map view, hide section view (within route planner screen)
  const mapContainer = document.getElementById('map-container')!;
  const sectionContainer = document.getElementById('section-container')!;
  mapContainer.style.display = 'block';
  sectionContainer.style.display = 'none';

  // Clean up plan view map
  if (planViewMap) {
    planViewMap.remove();
    planViewMap = null;
  }

  // Resize map after showing
  if (map) {
    setTimeout(() => map.resize(), 100);
  }
});

// Reset button
document.getElementById('reset-btn')!.addEventListener('click', () => {
  // Clear state
  currentRoute = null;
  elevationProfile = null;
  drawnFeatureId = null;

  // Clear draw features
  draw.deleteAll();

  // Remove curved route from map
  if (map.getSource('curved-route')) {
    map.removeLayer('curved-route-line');
    map.removeSource('curved-route');
  }

  // Reset UI
  updateInstructions('Click the line tool to draw a route, then drag points to adjust');
  document.getElementById('view-section-btn')!.setAttribute('disabled', 'true');
  document.getElementById('route-info')!.style.display = 'none';
  document.getElementById('section-view')!.innerHTML = '';

  // Start new drawing
  draw.changeMode('draw_line_string');
});

// Export button
document.getElementById('export-btn')!.addEventListener('click', () => {
  if (!elevationProfile || !hddPath) return;

  const startElevation = elevationProfile[0].elevation;
  const surveyInterval = 5; // meters

  // Helper function to calculate azimuth (bearing) between two points
  function calculateAzimuth(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const x = Math.sin(Δλ) * Math.cos(φ2);
    const y = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

    let bearing = Math.atan2(x, y) * 180 / Math.PI;
    // Normalize to 0-360
    bearing = (bearing + 360) % 360;
    return bearing;
  }

  // Helper function to interpolate coordinates along the profile
  function interpolateCoordinates(distance: number): { latitude: number; longitude: number; elevation: number } {
    if (distance <= 0) {
      return {
        latitude: elevationProfile![0].latitude,
        longitude: elevationProfile![0].longitude,
        elevation: elevationProfile![0].elevation
      };
    }
    if (distance >= elevationProfile![elevationProfile!.length - 1].distance) {
      const last = elevationProfile![elevationProfile!.length - 1];
      return { latitude: last.latitude, longitude: last.longitude, elevation: last.elevation };
    }

    // Find bracketing points
    for (let i = 0; i < elevationProfile!.length - 1; i++) {
      const p1 = elevationProfile![i];
      const p2 = elevationProfile![i + 1];
      if (p2.distance >= distance) {
        const t = (distance - p1.distance) / (p2.distance - p1.distance);
        return {
          latitude: p1.latitude + t * (p2.latitude - p1.latitude),
          longitude: p1.longitude + t * (p2.longitude - p1.longitude),
          elevation: p1.elevation + t * (p2.elevation - p1.elevation)
        };
      }
    }

    const last = elevationProfile![elevationProfile!.length - 1];
    return { latitude: last.latitude, longitude: last.longitude, elevation: last.elevation };
  }

  // Pre-calculate all survey data points
  interface SurveyPoint {
    md: number;
    hddPoint: HDDPathPoint;
    interpolatedCoords: { latitude: number; longitude: number; elevation: number };
  }

  const surveyPoints: SurveyPoint[] = [];
  for (let md = 0; md <= hddPath[hddPath.length - 1].distance; md += surveyInterval) {
    // Find the HDD path point closest to this MD
    let hddPoint = hddPath[0];
    for (const p of hddPath) {
      if (Math.abs(p.distance - md) < Math.abs(hddPoint.distance - md)) {
        hddPoint = p;
      }
    }

    // Interpolate coordinates at this exact MD
    const interpolatedCoords = interpolateCoordinates(md);

    surveyPoints.push({ md, hddPoint, interpolatedCoords });
  }

  // Generate survey points at regular intervals
  const surveys: string[][] = [];

  // Header - use current unit system
  const u = units.distanceUnit();
  surveys.push([
    'Station',
    `MD (${u})`,           // Measured Depth along bore
    `Bore Elev (${u})`,    // Bore path elevation (where the drill is)
    `TVD (${u})`,          // True Vertical Depth below entry point
    `Ground Elev (${u})`,  // Ground surface elevation at this point
    `Cover (${u})`,        // Depth of cover (ground minus bore = soil/rock above bore)
    'Inclination (°)',     // Angle from vertical (0=down, 90=horizontal, >90=ascending)
    'Azimuth (°)',         // Compass bearing (0-360, 0=North)
    `Dogleg (${units.doglegUnit()})`,      // Dogleg severity
    'Latitude',
    'Longitude'
  ]);

  surveyPoints.forEach((point, index) => {
    const { md, hddPoint, interpolatedCoords } = point;

    const tvd = startElevation - hddPoint.elevation;
    const cover = interpolatedCoords.elevation - hddPoint.elevation;

    // Convert angle from horizontal to inclination from vertical
    // hddPoint.angle: negative = descending, 0 = horizontal, positive = ascending
    // Inclination: 0° = vertical down, 90° = horizontal, >90° = ascending
    const inclination = 90 + hddPoint.angle;

    // Calculate azimuth to next point (or use previous for last point)
    let azimuth = 0;
    if (index < surveyPoints.length - 1) {
      const nextCoords = surveyPoints[index + 1].interpolatedCoords;
      azimuth = calculateAzimuth(
        interpolatedCoords.latitude, interpolatedCoords.longitude,
        nextCoords.latitude, nextCoords.longitude
      );
    } else if (index > 0) {
      // Use same azimuth as previous segment for last point
      const prevCoords = surveyPoints[index - 1].interpolatedCoords;
      azimuth = calculateAzimuth(
        prevCoords.latitude, prevCoords.longitude,
        interpolatedCoords.latitude, interpolatedCoords.longitude
      );
    }

    surveys.push([
      index.toString(),
      units.convertDistance(md).toFixed(1),
      units.convertElevation(hddPoint.elevation).toFixed(2),
      units.convertDepth(tvd).toFixed(2),
      units.convertElevation(interpolatedCoords.elevation).toFixed(2),
      units.convertDepth(cover).toFixed(2),
      inclination.toFixed(2),
      azimuth.toFixed(1),
      hddPoint.curvature.toFixed(2),
      interpolatedCoords.latitude.toFixed(6),
      interpolatedCoords.longitude.toFixed(6)
    ]);
  });

  // Add summary section
  const totalLength = hddPath[hddPath.length - 1].distance;
  const maxTVD = Math.max(...hddPath.map(p => startElevation - p.elevation));
  const minCover = Math.min(...hddPath!.slice(1, -1).map((p, i) => {
    const profileIdx = Math.floor((i / hddPath!.length) * elevationProfile!.length);
    return elevationProfile![profileIdx]?.elevation - p.elevation || 0;
  }));
  const maxDogleg = Math.max(...hddPath!.map(p => p.curvature));

  const summary = [
    '',
    'SURVEY SUMMARY',
    `Total Measured Depth,${units.convertDistance(totalLength).toFixed(1)} ${u}`,
    `Maximum TVD,${units.convertDepth(maxTVD).toFixed(2)} ${u}`,
    `Minimum Cover,${units.convertDepth(minCover).toFixed(2)} ${u}`,
    `Maximum Dogleg,${maxDogleg.toFixed(2)} ${units.doglegUnit()}`,
    `Entry Angle,${hddParameters.entryAngle}°`,
    `Exit Angle,${hddParameters.exitAngle || hddParameters.entryAngle}°`,
    `Hold Depth,${units.convertDepth(hddParameters.holdDepth).toFixed(1)} ${u}`,
    `Survey Interval,${units.convertDistance(surveyInterval).toFixed(1)} ${u}`,
    '',
    'PARAMETERS',
    `Kick-off Distance,${units.convertDistance(hddParameters.kickoffDistance).toFixed(1)} ${u}`,
    `Pull-back Distance,${units.convertDistance(hddParameters.pullbackDistance).toFixed(1)} ${u}`,
    `Min Clearance,${units.convertDepth(hddParameters.minClearance).toFixed(1)} ${u}`,
    `Max Dogleg Limit,${hddParameters.maxDogleg} ${units.doglegUnit()}`
  ];

  const csv = surveys.map(row => row.join(',')).join('\n') + '\n' + summary.join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'hdd-survey.csv';
  a.click();
  URL.revokeObjectURL(url);
});

// HDD Parameter Controls
const exitTypeSelect = document.getElementById('exit-type') as HTMLSelectElement;
const profileTypeSelect = document.getElementById('profile-type') as HTMLSelectElement;
const exitAngleGroup = document.getElementById('exit-angle-group')!;
const targetDepthGroup = document.getElementById('target-depth-group')!;
const holdDepthGroup = document.getElementById('hold-depth-group')!;
const kickoffGroup = document.getElementById('kickoff-group')!;
const pullbackGroup = document.getElementById('pullback-group')!;

// Update visibility based on profile type and exit type
function updateParameterVisibility() {
  const isContinuous = profileTypeSelect.value === 'continuous';
  const exitToSurface = exitTypeSelect.value === 'surface';

  // Continuous curve: show target depth, hide hold depth and curve length controls
  // Build/Hold/Build: show hold depth and curve length controls, hide target depth
  targetDepthGroup.style.display = isContinuous ? 'flex' : 'none';
  holdDepthGroup.style.display = isContinuous ? 'none' : 'flex';
  kickoffGroup.style.display = isContinuous ? 'none' : 'flex';
  pullbackGroup.style.display = (!isContinuous && exitToSurface) ? 'flex' : 'none';

  // Exit angle only shown when exiting to surface
  exitAngleGroup.style.display = exitToSurface ? 'flex' : 'none';

  // For underground exit without surface, show target depth regardless of profile type
  if (!exitToSurface) {
    targetDepthGroup.style.display = 'flex';
  }
}

exitTypeSelect.addEventListener('change', updateParameterVisibility);
profileTypeSelect.addEventListener('change', updateParameterVisibility);

// Recalculate HDD path button
document.getElementById('recalculate-hdd')!.addEventListener('click', () => {
  if (!elevationProfile) return;

  // Read parameters from inputs (convert from display units to meters)
  hddParameters = {
    entryAngle: parseFloat((document.getElementById('entry-angle') as HTMLInputElement).value),
    exitToSurface: exitTypeSelect.value === 'surface',
    exitAngle: parseFloat((document.getElementById('exit-angle') as HTMLInputElement).value),
    targetDepth: units.toMeters(parseFloat((document.getElementById('target-depth') as HTMLInputElement).value)),
    minClearance: units.toMeters(parseFloat((document.getElementById('min-clearance') as HTMLInputElement).value)),
    maxDogleg: parseFloat((document.getElementById('max-dogleg') as HTMLInputElement).value),
    holdDepth: units.toMeters(parseFloat((document.getElementById('hold-depth') as HTMLInputElement).value)),
    kickoffDistance: units.toMeters(parseFloat((document.getElementById('kickoff-distance') as HTMLInputElement).value)),
    pullbackDistance: units.toMeters(parseFloat((document.getElementById('pullback-distance') as HTMLInputElement).value)),
    profileType: (document.getElementById('profile-type') as HTMLSelectElement).value as 'continuous' | 'build-hold-build'
  };

  // Recalculate HDD path
  hddPath = calculateHDDPath(elevationProfile, hddParameters);

  // Re-render cross-section
  new CrossSectionView('#section-view', elevationProfile, {}, hddPath);
});

// Initialize HDD parameter inputs from defaults
function updateInclinationPreviews() {
  const entryAngle = parseFloat((document.getElementById('entry-angle') as HTMLInputElement).value) || 12;
  const exitAngle = parseFloat((document.getElementById('exit-angle') as HTMLInputElement).value) || 12;

  // Entry: going down, so inclination = 90 - entryAngle (from vertical)
  const entryInc = 90 - entryAngle;
  // Exit: going up, so inclination = 90 + exitAngle (from vertical)
  const exitInc = 90 + exitAngle;

  document.getElementById('entry-inc-preview')!.textContent = entryInc.toFixed(0);
  document.getElementById('exit-inc-preview')!.textContent = exitInc.toFixed(0);
}

function initHDDControls() {
  // Angles stay as-is (degrees)
  (document.getElementById('entry-angle') as HTMLInputElement).value = hddParameters.entryAngle.toString();
  (document.getElementById('exit-angle') as HTMLInputElement).value = (hddParameters.exitAngle || 12).toString();
  (document.getElementById('max-dogleg') as HTMLInputElement).value = hddParameters.maxDogleg.toString();

  // Distance/depth values need to be converted to display units
  (document.getElementById('target-depth') as HTMLInputElement).value = units.convertDepth(hddParameters.targetDepth || 15).toFixed(1);
  (document.getElementById('min-clearance') as HTMLInputElement).value = units.convertDepth(hddParameters.minClearance).toFixed(1);
  (document.getElementById('hold-depth') as HTMLInputElement).value = units.convertDepth(hddParameters.holdDepth).toFixed(1);
  (document.getElementById('kickoff-distance') as HTMLInputElement).value = units.convertDistance(hddParameters.kickoffDistance).toFixed(1);
  (document.getElementById('pullback-distance') as HTMLInputElement).value = units.convertDistance(hddParameters.pullbackDistance).toFixed(1);

  (document.getElementById('profile-type') as HTMLSelectElement).value = hddParameters.profileType;
  exitTypeSelect.value = hddParameters.exitToSurface ? 'surface' : 'underground';

  // Update inclination previews
  updateInclinationPreviews();

  // Update parameter visibility based on profile type
  updateParameterVisibility();

  // Add listeners to update previews when angles change
  document.getElementById('entry-angle')!.addEventListener('input', updateInclinationPreviews);
  document.getElementById('exit-angle')!.addEventListener('input', updateInclinationPreviews);
}

initHDDControls();

// Unit Toggle Functionality
function updateUnitLabels() {
  const unit = units.distanceUnit();
  const depthUnitStr = units.depthUnit();
  const doglegUnitStr = units.doglegUnit();

  // Update all unit labels in the DOM
  document.querySelectorAll('.distance-unit').forEach(el => {
    el.textContent = unit;
  });
  document.querySelectorAll('.depth-unit').forEach(el => {
    el.textContent = depthUnitStr;
  });
  document.querySelectorAll('.dogleg-unit').forEach(el => {
    el.textContent = doglegUnitStr;
  });

  // Update toggle button state
  const isImperial = !units.isMetric();
  const unitsCtrlBtn = document.getElementById('units-ctrl-btn');
  if (unitsCtrlBtn) {
    unitsCtrlBtn.textContent = units.isMetric() ? 'm' : 'ft';
    unitsCtrlBtn.classList.toggle('imperial', isImperial);
  }
  document.getElementById('unit-toggle-btn-section')?.classList.toggle('imperial', isImperial);

  // Update displayed values
  updateDisplayedValues();
}

function updateDisplayedValues() {
  // Update route distance display
  if (currentRoute) {
    document.getElementById('route-distance')!.textContent =
      units.convertDistance(currentRoute.distance).toFixed(1);
    document.getElementById('section-distance')!.textContent =
      units.convertDistance(currentRoute.distance).toFixed(1);
  }
}

function handleUnitToggle() {
  // Store current input values in meters before toggle
  const inputsToConvert = ['target-depth', 'hold-depth', 'kickoff-distance', 'pullback-distance', 'min-clearance'];
  const metersValues: Record<string, number> = {};

  inputsToConvert.forEach(id => {
    const input = document.getElementById(id) as HTMLInputElement;
    if (input) {
      // Convert current display value to meters
      metersValues[id] = units.toMeters(parseFloat(input.value) || 0);
    }
  });

  // Toggle units
  units.toggleUnits();

  // Update labels
  updateUnitLabels();

  // Convert input values to new display units
  inputsToConvert.forEach(id => {
    const input = document.getElementById(id) as HTMLInputElement;
    if (input && metersValues[id] !== undefined) {
      const converted = units.convertDistance(metersValues[id]);
      input.value = converted.toFixed(1);
    }
  });

  // Re-render section view if visible
  if (elevationProfile && hddPath) {
    new CrossSectionView('#section-view', elevationProfile, {}, hddPath);
  }
}

// Add event listener for section view unit toggle button
document.getElementById('unit-toggle-btn-section')?.addEventListener('click', handleUnitToggle);

// Initialize unit labels on load
updateUnitLabels();

} // End of initializeMap function

// Start the app
initApp();
